import type { InitOptions, OpenOptions } from "./types";
import { getState, setState } from "./state";
import {
  createOverlay,
  createModalContainer,
  createCloseButton,
  createIframe,
  createLoader,
} from "./modal";
import { buildIframeUrl, onIframeLoad } from "./iframe";
import { setupMessageListener } from "./messaging";

const DEFAULT_BASE_URL =
  import.meta.env.VITE_NBX_HUB_BASE_URL || "http://localhost:4200";

// Preloaded elements
let preloadedOverlay: HTMLDivElement | null = null;
let preloadedModal: HTMLDivElement | null = null;
let preloadedIframe: HTMLIFrameElement | null = null;
let storedInitOptions: InitOptions | null = null;

// Pending open call (when open() is called before init() completes)
let pendingOpenOptions: OpenOptions | null = null;
let pendingOpenResolver: (() => void) | null = null;

/**
 * Gets the stored init options
 */
export function getInitOptions(): InitOptions | null {
  return storedInitOptions;
}

/**
 * Gets the preloaded overlay element
 */
export function getPreloadedOverlay(): HTMLDivElement | null {
  return preloadedOverlay;
}

/**
 * Gets the preloaded modal element
 */
export function getPreloadedModal(): HTMLDivElement | null {
  return preloadedModal;
}

/**
 * Gets the preloaded iframe element
 */
export function getPreloadedIframe(): HTMLIFrameElement | null {
  return preloadedIframe;
}

/**
 * Gets and clears pending open options
 */
export function consumePendingOpen(): {
  options: OpenOptions;
  resolve: () => void;
} | null {
  if (pendingOpenOptions && pendingOpenResolver) {
    const result = {
      options: pendingOpenOptions,
      resolve: pendingOpenResolver,
    };
    pendingOpenOptions = null;
    pendingOpenResolver = null;
    return result;
  }
  return null;
}

/**
 * Sets pending open options (called from open() when state is loading)
 */
export function setPendingOpen(
  options: OpenOptions,
  resolve: () => void,
): void {
  pendingOpenOptions = options;
  pendingOpenResolver = resolve;
}

/**
 * Handles closing - called from index.ts
 */
export function setCloseHandler(handler: () => void): void {
  closeHandler = handler;
}

let closeHandler: (() => void) | null = null;

/**
 * Initializes the SDK by preloading the iframe
 */
export function init(options: InitOptions = {}): void {
  const state = getState();

  if (state !== "unloaded") {
    console.warn("[NbxHub] Already initialized");
    return;
  }

  console.log('[NbxHub SDK] 🚀 Iniciando SDK...');
  console.log('[NbxHub SDK] 📍 Estado: unloaded → loading');
  setState("loading");

  const baseUrl = options.baseUrl || DEFAULT_BASE_URL;
  // Store resolved options (with actual baseUrl) so executeOpen can use it
  storedInitOptions = { ...options, baseUrl };
  const feature = "midt";
  console.log('[NbxHub SDK] 🌐 Base URL:', baseUrl);

  // Create overlay (hidden)
  preloadedOverlay = createOverlay(() => {
    closeHandler?.();
  });
  preloadedOverlay.style.display = "none";
  preloadedOverlay.style.opacity = "0";

  // Create modal container
  preloadedModal = createModalContainer();
  preloadedModal.style.opacity = "0";
  preloadedModal.style.transform = "scale(0.95)";

  // Create close button
  const closeButton = createCloseButton(() => {
    closeHandler?.();
  });

  // Create loader
  const loader = createLoader();

  // Build iframe URL with minimal params (will send full config via postMessage)
  const iframeUrl = buildIframeUrl({
    baseUrl,
    feature,
    companyId: "preload",
  });

  console.log('[NbxHub SDK] 🖼️  Creando iframe con URL:', iframeUrl);

  // Create iframe
  preloadedIframe = createIframe(iframeUrl);

  // Assemble modal
  preloadedModal.appendChild(closeButton);
  preloadedModal.appendChild(loader);
  preloadedModal.appendChild(preloadedIframe);

  // Setup message listener
  const origin = new URL(baseUrl).origin;

  setupMessageListener(origin, {
    onReady: () => {
      console.log('[NbxHub SDK] 📥 Recibido NBX_READY del iframe');
      console.log('[NbxHub SDK] ✅ Estado cambiado a "ready"');
      setState("ready");
      options.onReady?.();

      // If there's a pending open, execute it
      const pending = consumePendingOpen();
      if (pending) {
        console.log('[NbxHub SDK] 🔄 Ejecutando open() pendiente');
        pending.resolve();
      }
    },
    onClose: () => {
      closeHandler?.();
    },
    onCloseCancelled: () => {
      // Noop during init phase - handled by open()
    },
    onMessage: () => {
      // Messages are handled by open() callbacks
    },
  });

  // Handle iframe load/error
  onIframeLoad(
    preloadedIframe,
    () => {
      // Iframe loaded, waiting for NBX_READY message
    },
    (error) => {
      setState("unloaded");
      cleanup();
      options.onError?.(error);
    },
  );

  // Add to DOM (hidden)
  document.body.appendChild(preloadedOverlay);
  preloadedOverlay.appendChild(preloadedModal);
}

/**
 * Cleans up preloaded elements
 */
function cleanup(): void {
  if (preloadedOverlay) {
    preloadedOverlay.remove();
  }
  preloadedOverlay = null;
  preloadedModal = null;
  preloadedIframe = null;
  storedInitOptions = null;
}

/**
 * Resets the init module (for testing)
 */
export function resetInit(): void {
  cleanup();
  pendingOpenOptions = null;
  pendingOpenResolver = null;
  closeHandler = null;
}
